<template>
  <AdditionalInfoModal
    :open="openAdditionalInfo"
    :additional_info="props.additional_info"
    :label="props.label"
    @close-modal="openAdditionalInfo = false"
  />
  <div
    class="relative w-full rounded-xl px-4 justify-between flex items-center overflow-visible border py-3 h-[72px]"
    :class="wrapper_class"
  >
    <div class="w-full min-w-0 overflow-visible">
      <!-- STRING / PASSWORD -->
      <template v-if="field_type === 'string' || field_type === 'password'">
        <div>
          <label v-if="label" :class="labelTextClass">
            {{ label }}
            <span> <Icon name="Asterisk" :class="requiredAsteriskClass" /> </span
          ></label>
          <input
            ref="input"
            :type="stringInputType"
            :value="modelValue"
            @input="updateValue($event.target.value)"
            @focus.prevent.stop="handleFocus"
            @blur.prevent.stop="handleBlur"
            @keydown="$emit('keydown', $event)"
            :readonly="read_only"
            :placeholder="place_holder || `Enter ${label}`"
            :class="inputStyles.control"
            :maxlength="max_length || undefined"
            :autocomplete="resolvedAutocomplete"
            :passwordrules="password_rules || undefined"
          />
        </div>
      </template>

      <!-- Yes/No -->
      <template v-else-if="field_type === 'yn'">
        <div class="relative">
          <label v-if="label" :class="labelTextClass">
            {{ label }}
          </label>

          <input
            ref="ynInput"
            type="text"
            :value="modelValue === true ? 'Yes' : 'No'"
            :readonly="read_only"
            @focus.prevent.stop="handleFocus"
            :class="[inputStyles.control]"
          />
        </div>
      </template>

      <!-- BOOLEAN -->
      <template v-else-if="field_type === 'boolean'">
        <label class="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            class="peer hidden"
            :checked="!!modelValue"
            @change="$emit('update:modelValue', $event.target.checked)"
          />

          <span
            class="h-[1.313rem] w-[1.303rem] rounded-[3px] border-2 border-radio-checkbox-outline grid place-content-center peer-checked:bg-radio-checkbox-checked peer-checked:border-radio-checkbox-checked"
          >
            <Icon
              name="Check"
              class="h-[1.125rem] w-[1.125rem]"
              :class="modelValue ? 'stroke-radio-checkbox-selector stroke-[3.5px]' : 'stroke-none'"
            />
          </span>

          <span v-if="label" class="font-inter text-strongest">{{ label }} </span>
        </label>
      </template>

      <!-- NUMBER -->
      <template v-else-if="field_type === 'number'">
        <div>
          <label v-if="label" :class="labelTextClass">
            {{ label }} <span> <Icon name="Asterisk" :class="requiredAsteriskClass" /> </span>
          </label>

          <div class="flex">
            <input
              ref="input"
              type="text"
              inputmode="decimal"
              :value="numberDisplay"
              @input="handleNumberInput($event)"
              @focus.prevent.stop="handleNumberFocus"
              @blur.prevent.stop="handleNumberBlur"
              :readonly="read_only"
              :placeholder="place_holder || `Enter ${label}`"
              :class="inputStyles.control"
              style="-moz-appearance: textfield"
            />
          </div>
        </div>
      </template>

      <!-- DATE -->
      <!-- <template v-else-if="field_type === 'date'">
        <div class="relative">
          <label v-if="label" :class="labelTextClass">
            {{ label }} <span> <Icon name="Asterisk" :class="requiredAsteriskClass" /> </span>
          </label>

          <input
            ref="dateInput"
            type="date"
            :value="formatted_input_value"
            @input="updateValue($event.target.value)"
            @change="validateInputValue($event.target.value)"
            @blur="handleBlur($event)"
            :disabled="read_only"
            @focus="handleFocus"
            :class="[
              inputStyles.control,
              'pr-10',
              'text-left',
              !formatted_input_value && !isInputFocused ? '!text-[#9ca3af]' : '',
            ]"
          />
        </div>
      </template> -->
      <template v-else-if="field_type === 'date'">
        <div class="relative">
          <label v-if="label" :class="labelTextClass">
            {{ label }} <span><Icon name="Asterisk" :class="requiredAsteriskClass" /></span>
          </label>

          <div class="relative flex items-center">
            <DatePickerRoot
              :model-value="calendarValue"
              :disabled="read_only"
              @update:model-value="onCalendarSelect"
            >
              <DatePickerField
                v-slot="{ segments }"
                :class="[inputStyles.control, 'pr-10 flex items-center']"
                @blur="handleBlur($event)"
              >
                <template v-for="item in segments" :key="item.part">
                  <DatePickerInput
                    :part="item.part"
                    :class="
                      item.part === 'literal'
                        ? ''
                        : 'rounded px-0.5 focus:outline-none focus:bg-muted data-[placeholder]:text-muted-foreground'
                    "
                  >
                    {{ item.value }}
                  </DatePickerInput>
                </template>
              </DatePickerField>
            </DatePickerRoot>

            <Popover v-model:open="isDatePickerOpen">
              <PopoverTrigger as-child>
                <button
                  v-if="!props.read_only"
                  class="absolute right-1 top-0 -translate-y-1/2"
                  size="sm"
                >
                  <Icon name="Calendar" class="h-4.5 w-4.5 stroke-cta-tertiary-outline-icon" />
                </button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0" align="start">
                <Calendar
                  class="rounded-md border shadow-sm [&_select]:min-w-[70px]"
                  layout="month-and-year"
                  :min-value="new CalendarDate(1915, 1, 1)"
                  :max-value="
                    field_name === 'date_of_birth' ? soonestDOB : new CalendarDate(2055, 1, 1)
                  "
                  :placeholder="
                    calendarValue
                      ? calendarValue
                      : field_name === 'date_of_birth'
                        ? calendarDefaultDOB
                        : field_name === 'id_expiration_date'
                          ? calendarDefaultIDExpiration
                          : calendarDefaultDate
                  "
                  :model-value="calendarValue"
                  @update:model-value="onCalendarSelect"
                  :initial-focus="true"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </template>

      <!-- DROPDOWN -->
      <template v-else-if="field_type === 'dropdown'">
        <div class="w-full">
          <label v-if="label" :class="labelTextClass"
            >{{ label }} <span> <Icon name="Asterisk" :class="requiredAsteriskClass" /> </span></label
          >
          <Listbox
            v-model="model"
            :disabled="read_only"
            as="div"
            class="w-full"
            @update:modelValue="(v) => validateInputValue(v?.name ?? v)"
            v-slot="{ open }"
          >
            <div
              class="flex items-center mt-[3px] mb-[3px]"
              :class="field_name == 'investment_objective' ? '' : 'max-h-[1.375rem]'"
            >
              <ListboxButton
                ref="listboxButton"
                :class="[
                  inputStyles.control,
                  'flex items-center justify-between',
                  read_only ? 'cursor-not-allowed' : 'cursor-pointer',
                ]"
                @focus="handleFocus"
                @blur="handleBlur"
              >
                <span
                  v-if="!(modelValue?.name || modelValue)"
                  class="text-gray-400 font-inter text-lg relative"
                >
                  {{ place_holder ? place_holder : `Select ${label}` }}
                </span>
                <span
                  v-else
                  :class="[
                    'text-base md:text-lg font-inter text-stronger flex items-center text-start',
                  ]"
                >
                  {{ displayedValueForDropdownOptions }}
                </span>
                <button
                  v-if="!props.read_only && props.field_type === 'dropdown'"
                  type="button"
                  class="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-cta-tertiary-outline-icon hover:bg-container-bg hover:text-strongest active:scale-95 transition-colors relative -top-[13px]"
                >
                  <Icon
                    name="ChevronDown"
                    class="h-5.5 w-5.5 transition-transform duration-300"
                    :class="open ? 'rotate-180' : 'rotate-0'"
                  />
                </button>
              </ListboxButton>
            </div>
            <Transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="opacity-0 translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-1"
            >
              <ListboxOptions :class="[dropdown_options_container]">
                <!-- options unchanged -->
                <ListboxOption
                  v-for="option in dropdown_options"
                  :key="option?.id || option?.name || option"
                  :value="option"
                  :disabled="!!option?.disabled"
                  v-slot="{ active, selected, disabled }"
                  as="template"
                  class="flex justify-between"
                >
                  <li
                    :class="[
                      'cursor-pointer truncate p-2 mb-3 last:mb-0 font-inter text-sm md:text-base text-strongest rounded-md',
                      active && !selected && !disabled
                        ? 'bg-paper-6 dark:bg-primary-3 dark:bg-opacity-30 bg-opacity-30'
                        : '',
                      selected ? 'bg-radio-checkbox-selector' : '',
                      disabled ? 'opacity-30 !cursor-not-allowed' : '',
                    ]"
                  >
                    <div class="inline-block font-inter">
                      {{ option?.name ? option.name : option }}
                      <p v-if="option?.subtext" class="text-xs font-inter text-light mt-0.5">
                        {{ option.subtext }}
                      </p>
                    </div>

                    <span v-if="selected">
                      <IconCheckmark class="h-5 w-5 fill-cta-tertiary-outline-icon mt-0.5" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </Transition>
          </Listbox>
        </div>
      </template>
    </div>

    <!-- BUTTON DIV -->

    <div class="flex items-center">
      <!-- <div v-if="props.read_only">
        <Icon name="Lock" class="h-[18px] w-[18px] stroke-strong" />
      </div> -->

      <!-- <button
        v-if="props.read_only"
        type="button"
        class="rounded-full flex items-center justify-center text-cta-tertiary-outline-icon"
      >
        <Icon name="Lock" class="h-4.5 w-4.5" />
      </button> -->
      <!-- <Button
        v-if="!props.read_only && props.field_type === 'date'"
        @click="openDatePicker"
        variant="darkpaper-primary"
        class="relative z-10"
        size="sm"
      >
        <template #icon>
          <Icon name="Calendar" class="h-4.5 w-4.5" />
        </template>
      </Button> -->
      <!-- <Button
        v-if="props.additional_info"
        variant="darkpaper-primary"
        @click="openAdditionalInfo = true"
        size="sm"
      >
        <template #icon>
          <Icon name="CircleQuestionMark" class="h-4.5 w-4.5" />
        </template>
      </Button> -->

      <template v-if="props.additional_info">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              as="button"
              type="button"
              class="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-cta-tertiary-outline-icon hover:bg-container-bg hover:text-strongest active:scale-95 transition-colors"
              @click="openAdditionalInfo = true"
            >
              <Icon name="CircleQuestionMark" class="h-4.5 w-4.5" />
            </TooltipTrigger>
            <TooltipContent
              :class="[
                '!bg-bg-canvas !text-strongest w-72 sm:w-80 rounded-2xl ring-1 ring-outline-dividers p-5 text-left shadow-lg',
                additional_info_tooltip_class,
              ]"
            >
              <div class="flex items-center gap-2 mb-2">
                <Icon name="CircleQuestionMark" class="h-3.5 w-3.5 stroke-info" />
                <span class="font-inter font-semibold text-sm text-info">{{ label }}</span>
              </div>
              <p class="font-inter text-sm text-strong leading-relaxed">
                {{ additional_info }}
              </p>
              <div class="my-4 h-px bg-outline-dividers" />
              <div class="flex items-center justify-between gap-3">
                <span class="font-inter text-sm text-strongest"
                  >Call us if you have any queries</span
                >
                <Button variant="paper-primary" size="xs" class="shrink-0" @click="callSupport">
                  <template #icon>
                    <Icon name="Phone" class="h-4 w-4" />
                  </template>
                  <template #label>Call {{ supportPhoneDisplay }}</template>
                </Button>
              </div>
              <TooltipArrow class="fill-bg-canvas stroke-outline-dividers stroke-1" />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </template>
      <button
        v-if="
          props.is_secure_field &&
          (props.field_type === 'string' || props.field_type === 'password')
        "
        type="button"
        class="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-cta-tertiary-outline-icon hover:bg-container-bg hover:text-strongest active:scale-95 transition-colors"
        @click.prevent.stop="toggleSecureVisibility"
        :aria-label="isSecureRevealed ? 'Hide value' : 'Show value'"
      >
        <Icon :name="isSecureRevealed ? 'EyeOff' : 'Eye'" class="h-4.5 w-4.5" />
      </button>
    </div>

    <!-- ERROR DIV -->
  </div>
  <div
    v-if="displayError"
    class="mt-2 ml-3 font-inter text-error text-sm flex items-center leading-[1.2rem]"
  >
    <Icon
      name="Asterisk"
      class="mr-1 h-[14px] min-h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-error"
    />
    {{ displayError }}
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue";
import Button from "@/components/Button.vue";

import { useSchemaStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";
import { registerFormField } from "@/composables/useFormAutoFocus";
import { Icon } from "@/components";
import {
  formatPhoneNumber,
  formatSsn,
  formatEIN,
  formatZipCode,
  commaSeparateThousands_2,
  autoCapitalize,
} from "@/utilities";
import { US_STATES } from "@/constants";
import { IconCheckmark, IconExpandMore } from "@/components/icons";
import AdditionalInfoModal from "./AdditionalInfoModal.vue";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDate, parseDate } from "@internationalized/date";
import { DatePickerRoot, DatePickerField, DatePickerInput } from "reka-ui";

const props = defineProps({
  modelValue: {
    type: [String, Number, Date, Array, Object, Boolean, null],
    default: "",
  },
  label: { type: String, default: "" },
  additional_info: { type: String, default: "" },
  field_name: { type: String, default: "" },
  field_type: { type: String, default: "text" },
  place_holder: { type: String, default: "" },
  number_type: { type: String, default: "text" },
  read_only: { type: Boolean, default: false },
  is_secure_field: { type: Boolean, default: false },
  size: { type: String, default: "md" },
  variant: { type: String, default: "primary" },
  dropdown_options: { type: Array, default: () => [] },
  dropdown_above: { type: Boolean, default: false },
  is_required: { type: Boolean, default: false },
  show_full_text: { type: Boolean, default: false },
  max_length: { type: [String, Number], default: null },
  error_message: { type: String, default: "" },
  record_id: { type: String, default: null },
  record_detail_group: { type: String, default: null },
  belongs_to_nested_object: { type: Boolean, default: false },
  nested_object_name: { type: String, default: "" },
  object_type: { type: String, default: null },
  autocomplete: { type: String, default: null },
  password_rules: { type: String, default: null },
  additional_info_tooltip_class: {
    type: String,
    default: "",
  },
});

const supportPhoneDisplay = "855-732-6395";
const supportPhone = "8557326395";

const callSupport = () => {
  window.location.href = `tel:${supportPhone}`;
};

const isInputFocused = ref(false);
const schemaStore = useSchemaStore();
const route = useRoute();
const { fieldValidationFailed } = useAnalytics();
const input = ref(null);
const inputError = ref("");
const hasErrorBefore = ref(false);
const openAdditionalInfo = ref(false);

const emit = defineEmits(["update:modelValue", "blur", "focus", "keydown"]);

const model = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

// Add a computed that merges external + internal errors
const displayError = computed(() => {
  return (
    props.error_message ||
    (inputError.value && schemaStore.show_input_errors ? inputError.value : "")
  );
});

/* Date Picker Logic */
const isDatePickerOpen = ref(false);

const soonestDOB = computed(() => {
  const d = new Date();
  return new CalendarDate(d.getFullYear() - 18, d.getMonth() + 1, d.getDate());
});

const calendarDefaultDOB = new CalendarDate(1950, 1, 1);

const calendarDefaultIDExpiration = computed(() => {
  const d = new Date();
  return new CalendarDate(d.getFullYear() + 3, d.getMonth() + 1, d.getDate());
});

const d = new Date();
const calendarDefaultDate = new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());

function toCalendarDate(v) {
  if (!v) return undefined;

  let dateStr = "";

  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    dateStr = v;
  } else {
    const d = typeof v === "string" ? new Date(v) : v;
    if (d instanceof Date && !isNaN(d.getTime())) {
      // build the string from LOCAL parts — no UTC/toISOString shift
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      dateStr = `${y}-${m}-${day}`;
    }
  }

  if (!dateStr) return undefined;
  try {
    return parseDate(dateStr);
  } catch {
    return undefined;
  }
}

// local source of truth for the picker UI
const internalCalendarValue = ref(toCalendarDate(props.modelValue));

const calendarValue = computed(() => internalCalendarValue.value);

function onCalendarSelect(value) {
  // always update local state so the UI reflects what's typed
  internalCalendarValue.value = value;

  // only bubble up a plausible, complete date — a lone digit in the
  // year segment (e.g. year = 2) shouldn't trigger a parent update
  // and round trip
  if (value && value.year >= 1000 && value.month && value.day) {
    emit("update:modelValue", value.toString()); // "YYYY-MM-DD"
  }
  if (value) {
    isDatePickerOpen.value = false;
  }
}

const isSecureRevealed = ref(false);

// Map schema field_name -> HTML autocomplete token. Without an affirmative
// token the browser guesses on address-shaped forms and field-shifts a saved
// profile (e.g. dropping first name into Street 1). An explicit `autocomplete`
// prop always wins so the auth pages keep "new-password"/"one-time-code".
// Note: `state`/`country` render as dropdowns (Listbox), so their tokens are
// inert here — kept for documentation only.
const AUTOCOMPLETE_BY_FIELD = {
  first_name: "given-name",
  last_name: "family-name",
  name: "name",
  email: "email",
  phone: "tel",
  date_of_birth: "bday",
  // residential address
  country: "country-name",
  street1: "address-line1",
  street2: "address-line2",
  city: "address-level2",
  state: "address-level1",
  zip_code: "postal-code",
  // mailing address — its own section so it doesn't fight the residential group
  ma_country: "section-mailing country-name",
  ma_street1: "section-mailing address-line1",
  ma_street2: "section-mailing address-line2",
  ma_city: "section-mailing address-level2",
  ma_state: "section-mailing address-level1",
  ma_zip_code: "section-mailing postal-code",
  // sensitive — discourage the browser from storing/autofilling
  ssn: "off",
  ein: "off",
  id_number: "off",
};

const resolvedAutocomplete = computed(
  () => props.autocomplete || AUTOCOMPLETE_BY_FIELD[props.field_name] || undefined
);

const stringInputType = computed(() => {
  // If it's a secure string field, toggle between password dots and text
  if (props.is_secure_field && (props.field_type === "string" || props.field_type === "password")) {
    return isSecureRevealed.value ? "text" : "password";
  }

  // Otherwise keep your existing behavior
  return props.field_type === "password" ? "password" : "text";
});

function toggleSecureVisibility() {
  // Read the CURRENT state from the live DOM rather than trusting our ref.
  // Browsers (Chrome/iOS) manage the `type` of autocomplete="new-password" fields themselves — revealing on AutoFill, re-masking on focus/blur — so our ref can drift out of sync with what the field is actually showing.
  // Basing the toggle on the real DOM type makes the eyeball always do the intuitive thing (and fixes the "first click does nothing" symptom).
  const el = input.value;
  const currentlyRevealed = el ? el.type === "text" : isSecureRevealed.value;
  isSecureRevealed.value = !currentlyRevealed;

  // Note: intentionally NOT re-focusing the input here. Re-focusing re-triggers
  // the browser's own focus-driven reveal/mask behavior, which fights this
  // toggle and causes the visibility to flip on its own.
}

// Keep our reveal state (and therefore the eyeball icon) in sync with the DOM.
// Because both password fields are autocomplete="new-password" inside a <form>,
// the browser flips the input's `type` attribute imperatively — outside Vue's
// knowledge — on AutoFill and on focus/blur. A MutationObserver on the `type`
// attribute lets us mirror those changes back into isSecureRevealed so the icon
// never lies. Setting the ref to a value it already holds is a no-op, so the
// observer/computed pair converges rather than looping.
let secureTypeObserver = null;

onMounted(() => {
  if (!props.is_secure_field) return;
  const el = input.value;
  if (!el || typeof MutationObserver === "undefined") return;

  isSecureRevealed.value = el.type === "text";

  secureTypeObserver = new MutationObserver(() => {
    const revealed = el.type === "text";
    if (revealed !== isSecureRevealed.value) {
      isSecureRevealed.value = revealed;
    }
  });
  secureTypeObserver.observe(el, {
    attributes: true,
    attributeFilter: ["type"],
  });
});

onBeforeUnmount(() => {
  secureTypeObserver?.disconnect();
  secureTypeObserver = null;
});

const inputStyles = computed(() => {
  const sizeKey = props.size || "md";

  const borderClass = props.read_only
    ? "border-neutral-gray-5"
    : schemaStore.show_input_errors
      ? "!border-functional-error-default focus:border-functional-error-default"
      : isInputFocused.value
        ? "border-primary-6"
        : "border-neutral-gray-5";

  const text_color = props.read_only ? "text-strong" : "text-strongest";

  return {
    control: [
      "w-full  z-0  bg-transparent focus:outline-none focus:ring-0 text-base md:text-lg font-inter  leading-[1.2rem]",
      text_color,
      borderClass,
      props.read_only ? "cursor-not-allowed" : "",
    ].join(" "),
  };
});

const requiredAsteriskClass = computed(() => {
  // if disabled or read only return 'hidden'
  if (props.is_disabled || props.read_only || !props.is_required) {
    return "hidden";
  }

  return "text-error h-4 w-4";
});
const labelTextClass = computed(() => {
  let text_color = "text-stronger opacity-70";

  if (
    props.error_message ||
    (displayError.value && schemaStore.show_input_errors) ||
    (expired_id.value && props.read_only)
  ) {
    text_color = "!text-error";
  }

  return (
    "items-center pointer-events-none  flex z-10 font-inter text-sm md:text-base " +
    text_color +
    " mb-[2px]"
  );
});
const dropdown_options_container = computed(() => {
  let base =
    "absolute left-0 right-0 z-[9999] mt-3 max-h-60 overflow-auto rounded-2xl p-3 border border-outline-dividers bg-bg-canvas text-dark shadow-[0_18px_26px_0_var(--container-bg)]";

  let dropdown_above = props.dropdown_above ? "bottom-full mb-2 mt-0" : "";

  return [base, dropdown_above].join(" ");
});

const expired_id = computed(() => {
  let is_expired = false;

  if (props.field_name === "id_expiration_date") {
    let id_expiration_date = props.modelValue;
    // id_expiration_date = "2026-03-04";
    if (!id_expiration_date) return;
    const today = new Date();
    const expirationDate = new Date(id_expiration_date);
    const timeDiff = expirationDate.getTime() - today.getTime();
    let days = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return days <= 30;
  }
});

const wrapper_class = computed(() => {
  // let bg = {
  //   primary: "bg-container-bg-dis-canvas",
  //   secondary: "bg-container-bg",
  // };

  // let border = {
  //   primary: "border-container-bg-dis-canvas",
  //   secondary: "border-container-bg",
  // };

  let bg_for_return = "bg-input-v2-bg";

  if (props.read_only) {
    bg_for_return = "bg-input-v2-bg-disabled";
  }

  let border_for_return = "border-input-v2-stroke";

  if (
    props.error_message ||
    (displayError.value && schemaStore.show_input_errors) ||
    (expired_id.value && props.read_only)
  ) {
    border_for_return = "!border-outline-error";
  }

  if (props.field_type === "boolean") {
    bg_for_return = "bg-transparent";
    border_for_return = "border-transparent";
  }

  const focus_within_class = props.read_only
    ? ""
    : "focus-within:border-filter-outline-selected-chip-icon";

  return [bg_for_return, border_for_return, focus_within_class].join(" ");
});

const formatted_input_value = computed(() => {
  const v = props.modelValue;
  if (!v) return "";

  // Already correct: YYYY-MM-DD
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

  // ISO string: 2026-02-02T...
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return "";
  }

  // Date object
  if (v instanceof Date && !isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }

  return "";
});

const displayedValueForDropdownOptions = computed(() => {
  if (typeof props.modelValue === "object" && props.modelValue?.name) {
    return props.modelValue.name;
  }
  return props.modelValue;
});

const handleFocus = () => {
  isInputFocused.value = true;
  emit("focus");
};

const listOfAmountFields = [
  "amount",
  "current_net_worth",
  "current_liquid_net_worth",
  "current_income",
  "amountBonds",
];

const updateValue = (value) => {
  let v = value ?? "";

  if (props.field_name === "phone") {
    v = formatPhoneNumber(v);
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v)); // was allowing letter inputs without this
    }
  }

  if (props.field_name === "ssn") {
    v = formatSsn(v);
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  if (props.field_name === "ein") {
    v = formatEIN(v);
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  if (listOfAmountFields.includes(props.field_name)) {
    v = v.toString().replace(/[^0-9.]/g, "");
    const vNoZeroes = v.replace(/^0+(?=\d)/, "");
    v = commaSeparateThousands_2(vNoZeroes);
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  if (props.field_name === "zip_code" || props.field_name === "ma_zip_code") {
    v = formatZipCode(v);
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  if (
    props.field_name === "street1" ||
    props.field_name === "street2" ||
    props.field_name === "ma_street1" ||
    props.field_name === "ma_street2"
  ) {
    v = v.toString().replace(/[^a-zA-Z0-9\s.,#'-]/g, "");
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  if (props.field_name === "id_number") {
    v = v.toString().replace(/[^a-zA-Z0-9-]/g, "");
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  const listOfNonNumberFields = ["first_name", "last_name", "city", "id_issued_by"];

  const listOfCapitalizedFields = [
    "first_name",
    "name",
    "last_name",
    "occupation",
    "employer",
    "city",
    "id_issued_by",
    "source_of_income",
  ];

  if (listOfCapitalizedFields.includes(props.field_name)) {
    v = autoCapitalize(v.toString());
  }

  if (listOfNonNumberFields.includes(props.field_name)) {
    v = v.toString().replace(/[^a-zA-Z\s]/g, "");
    const el = input.value;
    if (el) {
      queueMicrotask(() => (el.value = v));
    }
  }

  if (props.field_type === "date") {
    if (typeof value === "string") {
      v = value;
    } else {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        date.setHours(0, 0, 0, 0);
        v = date.toISOString().slice(0, 10);
      } else {
        v = "";
      }
    }
  }

  if (props.field_type === "number") {
    v = value === "" || value == null ? "" : Number(value);
  }

  if (hasErrorBefore.value) validateInputValue(v);

  emit("update:modelValue", v);
};

const validateInputValue = (value) => {
  // console.log('validating input field "', props.field_name, '" with value:', value);
  let isValid = true;

  let record = {
    [props.field_name]: value,
  };

  if (props.belongs_to_nested_object && props.nested_object_name) {
    record = {
      [props.nested_object_name]: {
        [props.field_name]: value,
      },
    };
  }

  let schema = [
    {
      field_name: props.field_name,
      field_type: props.field_type,
      label: props.label,
      is_required: props.is_required,
      record_detail_group: props.record_detail_group,
      is_required: props.is_required,
      belongs_to_nested_object: props.belongs_to_nested_object,
      nested_object_name: props.nested_object_name,
    },
  ];

  let validation_check = schemaStore.validateFields(
    record,
    schema,
    props.record_id,
    false,
    props.object_type
  );

  isValid = validation_check.proceed;

  // if (!isValid) console.log("Validation check result:", validation_check);

  const wasValid = !inputError.value;
  inputError.value = isValid ? "" : validation_check.error_messages[0].message || "Invalid input";
  if (!isValid && wasValid) {
    fieldValidationFailed(props.field_name, { form: route.path, message: inputError.value });
  }

  return isValid;
};

const numberDraft = ref(""); // what the user sees while typing

const isNumberField = computed(() => props.field_type === "number");

const numberDisplay = computed(() => {
  // While focused, show the draft string (so caret/typing feels normal)
  if (isInputFocused.value && isNumberField.value) return numberDraft.value;

  // When not focused, format from the real modelValue
  return formatNumberForDisplay(props.modelValue, props.number_type);
});

/**
 * Takes a numeric (or ""/null) modelValue and returns the string shown in the input.
 */
function formatNumberForDisplay(value, numberType) {
  if (value === "" || value == null || Number.isNaN(value)) return "";

  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return "";

  // Limit currencies to 2 decimals, but allow integers to show without decimals.
  if (numberType === "currency") {
    const isEvenDollar = Number.isInteger(n);
    const options = isEvenDollar
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    const formatted = new Intl.NumberFormat("en-US", options).format(n);
    return `$ ${formatted}`;
  }

  let options = { maximumFractionDigits: 20 };
  const formatted = new Intl.NumberFormat("en-US", options).format(n);

  if (numberType === "percentage") return `${formatted}%`;
  return formatted;
}

/**
 * Takes the input string (with $ , %) and returns:
 *  - { num: Number | "", cleaned: string }
 * cleaned is a normalized string version you can reformat for display.
 */
function parseNumberFromInput(raw, numberType) {
  let s = (raw ?? "").toString();
  s = s.replace(/\s/g, "");
  if (numberType === "currency") s = s.replace(/\$/g, "");
  if (numberType === "percentage") s = s.replace(/%/g, "");
  s = s.replace(/,/g, "");
  s = s.replace(/[^\d.]/g, "");

  if (s === "" || s === ".") {
    return { num: "", cleaned: s };
  }

  const num = Number(s);
  if (Number.isNaN(num)) return { num: "", cleaned: "" };

  return { num, cleaned: s };
}

/**
 * Reformat the input as the user types, and emit the numeric value.
 * (Basic caret handling: preserves caret from jumping to the end when commas are inserted.)
 */
function handleNumberInput(e) {
  const el = e.target;
  const prev = el.value;
  const prevCaret = el.selectionStart ?? prev.length;

  // Strip letters and special characters
  let raw = prev.replace(/[^0-9]/g, "");

  const { num, cleaned } = parseNumberFromInput(raw, props.number_type);

  const display =
    cleaned === "" || cleaned === "-" || cleaned === "." || cleaned === "-."
      ? cleaned
      : formatNumberForDisplay(num, props.number_type);

  numberDraft.value = display;

  emit("update:modelValue", num);

  // Force the element value to the sanitized display
  queueMicrotask(() => {
    el.value = display;
    const delta = display.length - prev.length;
    const nextCaret = Math.max(0, Math.min(display.length, prevCaret + delta));
    el.setSelectionRange(nextCaret, nextCaret);
  });

  if (hasErrorBefore.value) validateInputValue(num);
}

function handleNumberFocus() {
  isInputFocused.value = true;

  // Start draft from current modelValue, but show it nicely for typing.
  // Optional: you might prefer "raw" without $/% on focus—if so, remove the format call here.
  numberDraft.value = formatNumberForDisplay(props.modelValue, props.number_type);

  emit("focus");
}

function handleNumberBlur() {
  isInputFocused.value = false;

  // On blur, snap draft to the formatted modelValue
  numberDraft.value = formatNumberForDisplay(props.modelValue, props.number_type);

  validateInputValue(props.modelValue);
  emit("blur");
}

// Keep draft in sync if parent changes modelValue while you're not typing
watch(
  () => props.modelValue,
  (v) => {
    if (!isInputFocused.value && isNumberField.value) {
      numberDraft.value = formatNumberForDisplay(v, props.number_type);
    }
    if (props.field_type == "date") {
      // avoid feedback loop
      const incoming = toCalendarDate(v);
      const incomingStr = incoming ? incoming.toString() : "";
      const currentStr = internalCalendarValue.value ? internalCalendarValue.value.toString() : "";
      if (incomingStr !== currentStr) {
        internalCalendarValue.value = incoming;
      }
    }
  }
);

watch(
  () => props.error_message,
  (msg) => {
    if (msg) inputError.value = msg;
  }
);
// const shouldValidateOnPropChange = computed(() => {
//   // validate dates + any other fields you want to validate immediately
//   return props.field_type === "date";
// });

const getValueForValidation = () => {
  // Dropdowns: validate the selected option (string or object.name)
  if (props.field_type === "dropdown") {
    return props.modelValue?.name ?? props.modelValue ?? "";
  }

  // Dates: validate what the date input would display (YYYY-MM-DD)
  if (props.field_type === "date") {
    return formatted_input_value.value; // already computed as "YYYY-MM-DD" or ""
  }

  // Numbers: validate the underlying modelValue (a Number or "")
  if (props.field_type === "number") {
    return props.modelValue ?? "";
  }

  // Strings/password/etc
  return props.modelValue ?? "";
};

watch(
  () => props.modelValue,
  () => {
    // console.log(
    //   'prop modelValue changed for field "',
    //   props.field_name,
    //   '", new value:',
    //   props.modelValue
    // );

    const raw = getValueForValidation();
    validateInputValue(raw);
  },
  { immediate: true }
);

const handleBlur = (e) => {
  isInputFocused.value = false;

  // Dropdown blur: DON'T use e.target.value (it's a button)
  if (props.field_type === "dropdown") {
    const selected = props.modelValue?.name ?? props.modelValue ?? "";
    validateInputValue(selected);
    emit("blur");
    return;
  }

  // Date blur: validate what the input is showing
  if (props.field_type === "date") {
    const raw = e?.target?.value; // "YYYY-MM-DD"
    validateInputValue(raw);
    emit("blur");
    return;
  }

  // Everything else (string/number text inputs)
  const raw = e?.target?.value ?? props.modelValue ?? "";
  validateInputValue(raw);

  emit("blur");
};

// ── Autofocus support ──────────────────────────────────────────────────
const focus = () => {
  if (props.read_only) return;

  const el =
    props.field_type === "dropdown"
      ? listboxButton.value?.$el
      : props.field_type === "date"
        ? dateInput.value
        : input.value;

  el?.focus();
};

defineExpose({ focus });

registerFormField(() => focus(), props.read_only);
</script>
<style scoped>
/* Remove number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Keep native appearance on iOS */
input[type="date"] {
  -webkit-appearance: auto;
  appearance: auto;
  text-align: left; /* ensure base alignment */
}

/* Only remove appearance on non-iOS browsers */
@supports not (-webkit-touch-callout: none) {
  input[type="date"] {
    -webkit-appearance: none;
    appearance: none;
  }
}

/* Hide the native calendar icon but KEEP its space */
input[type="date"]::-webkit-calendar-picker-indicator {
  opacity: 0;
  width: 1.25rem;
  height: 1.25rem;
}

/* Force left alignment for Safari internal date fields */
input[type="date"]::-webkit-datetime-edit {
  text-align: left;
}

input[type="date"]::-webkit-datetime-edit-fields-wrapper {
  justify-content: flex-start;
}

input[type="date"]::-webkit-datetime-edit-text,
input[type="date"]::-webkit-datetime-edit-month-field,
input[type="date"]::-webkit-datetime-edit-day-field,
input[type="date"]::-webkit-datetime-edit-year-field {
  text-align: left;
}
</style>
